import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as workflowApi from "./api";

export function useWorkflows(moduleCode?: string) {
  return useQuery({
    queryKey: ["workflows", moduleCode],
    queryFn: () => workflowApi.getWorkflows(moduleCode),
  });
}

export function useWorkflow(id: number) {
  return useQuery({
    queryKey: ["workflow", id],
    queryFn: () => workflowApi.getWorkflow(id),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function useCreateWorkflow() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: workflowApi.createWorkflow,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workflows"] }),
  });
}

export function useUpdateWorkflow() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: any) => workflowApi.updateWorkflow(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workflows"] });
      qc.invalidateQueries({ queryKey: ["workflow", vars.id] });
    },
  });
}

export function useDeleteWorkflow() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: workflowApi.deleteWorkflow,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workflows"] }),
  });
}

export function usePublishWorkflow() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: workflowApi.publishWorkflow,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["workflows"] });
      qc.invalidateQueries({ queryKey: ["workflow", id] });
    },
  });
}

export function useActivateWorkflow() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: workflowApi.activateWorkflow,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["workflows"] });
      qc.invalidateQueries({ queryKey: ["workflow", id] });
    },
  });
}

export function useDeactivateWorkflow() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: workflowApi.deactivateWorkflow,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["workflows"] });
      qc.invalidateQueries({ queryKey: ["workflow", id] });
    },
  });
}

export function useAddWorkflowStep() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, payload }: any) =>
      workflowApi.addWorkflowStep(workflowId, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workflow", vars.workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}

export function useUpdateWorkflowStep() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, stepId, payload }: any) =>
      workflowApi.updateWorkflowStep(workflowId, stepId, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workflow", vars.workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}

export function useDeleteWorkflowStep() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, stepId }: any) =>
      workflowApi.deleteWorkflowStep(workflowId, stepId),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workflow", vars.workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}

export function useAddWorkflowRule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, payload }: any) =>
      workflowApi.addWorkflowRule(workflowId, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workflow", vars.workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}

export function useUpdateWorkflowRule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, ruleId, payload }: any) =>
      workflowApi.updateWorkflowRule(workflowId, ruleId, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workflow", vars.workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}

export function useDeleteWorkflowRule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, ruleId }: any) =>
      workflowApi.deleteWorkflowRule(workflowId, ruleId),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workflow", vars.workflowId] });
      qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}

export function useDelegations(moduleCode?: any) {
  return useQuery({
    queryKey: ["workflow-delegations", moduleCode],
    queryFn: () => workflowApi.getDelegations(moduleCode),
  });
}

export function useCreateDelegation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: workflowApi.createDelegation,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["workflow-delegations"] }),
  });
}

export function useUpdateDelegation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: any) =>
      workflowApi.updateDelegation(id, payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["workflow-delegations"] }),
  });
}

export function useDeleteDelegation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: workflowApi.deleteDelegation,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["workflow-delegations"] }),
  });
}

export function useWorkflowOptions() {
  return useQuery({
    queryKey: ["workflow-options"],
    queryFn: async () => {
      const [departments, roles, users] = await Promise.all([
        workflowApi.getDepartmentOptions(),
        workflowApi.getRoleOptions(),
        workflowApi.getUserOptions(),
      ]);

      return { departments, roles, users };
    },
    staleTime: 5 * 60 * 1000,
  });
}